import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import IconSvg from 'Components/IconSvg';

const formatTabs = (tabs, strings) => {
  return tabs.map((tab) => {
    const setLabel = (key) => ({ label: strings[key.toLowerCase()] || key });
    if (typeof tab === 'string')
      return {
        key: tab,
        ...setLabel(tab),
      };
    return {
      ...tab,
      ...setLabel(tab.key),
    };
  });
};

const Nav = ({ activeTab, alignment, onClick, strings, tabs }) => {
  const containerEl = useRef(null);
  const [index, setIndex] = useState(0);
  const [nodeStyles, setNodeStyles] = useState([]);
  const formattedTabs = formatTabs(tabs, strings);
  // Value to transformX the nav indicator
  // Calculate based on previous tab widths and margins for dynamic positioning
  const offset = nodeStyles.reduce((acc, tab, idx) => {
    if (index > idx) {
      const prevTab = nodeStyles[idx];
      acc += prevTab.width + prevTab.marginRight + tab.marginLeft;
    }
    return acc;
  }, nodeStyles[index]?.marginLeft ?? 0);

  const isActiveTab = (key) => activeTab.includes(key);

  const calculateIndex = () => {
    const index = formattedTabs.findIndex(({ key }) => isActiveTab(key));
    setIndex(index);
  };

  const calculateNodeStyles = async () => {
    // Horizontal Nav parent may be hidden when in desktop view,
    // clone parent element (to ensure all style rules are applied)
    // and add/remove from body to ensure accurate dimensions.
    // Wait for fonts to be loaded to prevent miscalculation
    await document?.fonts?.ready;
    const cloneParentEl = containerEl.current.parentNode.cloneNode(true);
    cloneParentEl.style.display = 'block';
    document.body.appendChild(cloneParentEl);
    const linkNodes = cloneParentEl.querySelectorAll('.nav__item');
    const nodeStyles = [...linkNodes].map((node) => {
      const { marginLeft, marginRight, width } = getComputedStyle(node);
      return {
        marginLeft: parseFloat(marginLeft),
        marginRight: parseFloat(marginRight),
        width: parseFloat(width),
      };
    });
    document.body.removeChild(cloneParentEl);
    setNodeStyles(nodeStyles);
  };

  const renderTabBody = (icon, label) => (
    <>
      {icon && (
        <span className="nav__icon">
          <IconSvg type={icon} />
        </span>
      )}
      <span className="nav__text">{label}</span>
    </>
  );

  const renderTab = ({ href, icon, key, label }) => {
    const className = `nav__item ${isActiveTab(key) ? 'active' : ''}`;
    const handleClick = () => onClick(key);
    if (href)
      return (
        <Link className={className} key={key} onClick={handleClick} to={href}>
          {renderTabBody(icon, label)}
        </Link>
      );

    return (
      <span className={className} key={key} onClick={handleClick}>
        {renderTabBody(icon, label)}
      </span>
    );
  };

  const scrollTabIntoView = () => {
    // On mobile view tabs may expand past visible width,
    // scroll active tab into view in that case
    const navItemEl = containerEl?.current?.querySelectorAll('.nav__item')[
      index
    ];
    navItemEl?.scrollIntoView(false);
  };

  useLayoutEffect(() => {
    if (alignment === 'horizontal') calculateNodeStyles();
  }, []);

  useEffect(() => {
    calculateIndex();
  }, [activeTab]);

  useEffect(() => {
    if (alignment === 'horizontal') scrollTabIntoView();
  }, [index]);

  return (
    <div ref={containerEl} className={`nav--${alignment}`}>
      {formattedTabs.map(renderTab)}
      {alignment === 'horizontal' && (
        <div
          className="nav__indicator"
          style={{
            transform: `translateX(${offset}px)`,
            width: `${nodeStyles[index]?.width ?? 0}px`,
          }}
        />
      )}
    </div>
  );
};

Nav.propTypes = {
  activeTab: PropTypes.string.isRequired,
  alignment: PropTypes.oneOf(['vertical', 'horizontal']),
  onClick: PropTypes.func,
  strings: PropTypes.object.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        icon: PropTypes.string,
        key: PropTypes.string.isRequired,
      }),
    ])
  ).isRequired,
};

Nav.defaultProps = {
  alignment: 'horizontal',
  onClick: () => {},
};

export default Nav;
